// src/oura/screens/Delivery.tsx
//
// Delivery: what we owe this client, deliverable by deliverable. It replaces
// Fixes (the weekly step list) and folds in Plan (the contract and the
// timeline). One line per deliverable with its next step and who takes it;
// a Checklist toggle shows every item as a square; By month is the timeline
// from real states; Plan holds the key pages, the ledger and the contract.
//
// Deep links: ?d=<deliverable key> opens its drawer, ?view=checklist|month|plan
// picks the view, ?section= scrolls Plan to a section (the key pages, the
// outlet names).
//
// People do the work; Oura's scan or records prove it. Nothing here shows
// Oura doing work.
//
// Behind the Docs write switch, a deliverable's "Create the doc" opens Create
// a doc in the same drawer, with Back to the deliverable, and a docs write
// reads Delivery again, so the step moves on to "Read it, then share".

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarRangeIcon, DownloadIcon, ListChecksIcon, ListIcon, NotebookPenIcon, RefreshCwIcon, Table2Icon } from "lucide-react";
import { TasksView } from "../tasks/TasksView";
import { TasksLive } from "../tasks/TasksLive";
import { SAMPLE_PHASES, SAMPLE_SHEET_URL } from "../tasks/sample";
import { TASKS_LIVE, TASKS_ON } from "../tasks/flag";
import type { DeliveryGroup, DeliveryPayload } from "@service/aeo/delivery";
import { checkNow, dayLabel, isDeliveryServed } from "@service/aeo/delivery";
import { ScreenSentence } from "../components/client/ScreenSentence";
import { cn } from "../lib/cn";
import { Drawer, FoldCard, FoldScope, PillButton, Seg, Tag, TYPE, drawerNav, drawerSequence, type DrawerDone } from "../ui";
import { ByMonth } from "../delivery/ByMonth";
import { DeliverableSheet } from "../delivery/DeliverableDrawer";
import { DeliveryList } from "../delivery/DeliveryList";
import { RequestRows } from "../delivery/RequestRows";
import { useChangeRequests } from "@oura/docs";
import { groupRows, type ListMode } from "../delivery/listModel";
import { NoPlanYet } from "../delivery/NoPlanYet";
import { PlanView } from "../delivery/PlanView";
import { SquareKey } from "../delivery/Square";
import { GROUP_TITLE, plural } from "../delivery/format";
import { downloadDeliveryTimeline } from "../delivery/timelineDownload";
import { useDelivery } from "../delivery/useDelivery";
import { CreateFromDelivery } from "../delivery/CreateFromDelivery";
import type { CreatePrefill } from "../components/docs/create/createModel";
import { PLAN_SECTION_ID, isPlanSection, type PlanSection } from "../components/plan/planModel";
import { DOCS_WRITE_ON } from "../docs/writeFlag";
import { subscribeDocs } from "@service/aeo/clientDocs";
import { ASK_OURA_ON, AskOuraSuggestion } from "../ask";
import { useOuraRole } from "../role";

type View = "next" | "checklist" | "tasks" | "month" | "plan";

/**
 * Checklist and By month, hidden (not deleted) since Tasks took their place
 * (#4727, 27 Oct 2026). True brings both tabs back exactly as they were. While
 * hidden, a stored or linked `checklist`/`month` view opens Next steps, so
 * nobody lands on a tab they cannot see. The published timeline that By month
 * carried (Publish, Dates, Schedule) lives on Plan either way.
 */
const LEGACY_VIEWS_ON = false;

const VIEW_KEY = "oura:delivery-view";

function readView(): View {
    try {
        const v = window.localStorage.getItem(VIEW_KEY);
        return LEGACY_VIEWS_ON && v === "checklist" ? "checklist" : "next";
    } catch {
        return "next";
    }
}

export interface DeliveryProps {
    businessId: string;
    businessName: string;
    /** Open another staff view of this business ("reviews", "setup", "reports", ...). */
    onGoTo: (view: string, search?: string) => void;
    /** The contract editor, as the workspace renders it today. */
    contract: ReactNode;
    /**
     * The engine's dated schedule with its review and Publish (#3757), as the
     * Timeline tab rendered it. Plan shows it (By month did until #4727), so
     * publishing the dates the client is told survives Timeline folding into Delivery.
     */
    timeline?: ReactNode;
}

export function Delivery({ businessId, businessName, onGoTo, contract, timeline }: DeliveryProps) {
    const role = useOuraRole();
    const { load, apply, reload } = useDelivery(businessId, businessName);
    // The client's change requests sit on the same list as the plan's work.
    const requests = useChangeRequests(businessId);
    const [params, setParams] = useSearchParams();
    const urlView = params.get("view");
    const [listMode, setListMode] = useState<ListMode>(readView() === "checklist" ? "checklist" : "next");
    const view: View =
        (LEGACY_VIEWS_ON && urlView === "month") || urlView === "plan" || (TASKS_ON && urlView === "tasks")
            ? urlView
            : LEGACY_VIEWS_ON && urlView === "checklist"
              ? "checklist"
              : listMode;
    const openKey = params.get("d");
    const [openGroup, setOpenGroup] = useState<DeliveryGroup | null>(null);
    /** The group's rows when the drawer opened: previous and next walk this, as the drawer kit expects. */
    const [openSeq, setOpenSeq] = useState<string[]>([]);
    const [checkMsg, setCheckMsg] = useState<string | null>(null);
    /** Create a doc, open over the deliverable it came from; `n` keys a fresh sheet for Next. */
    const [creating, setCreating] = useState<{ prefill: CreatePrefill; n: number } | null>(null);
    const [checkBusy, setCheckBusy] = useState(false);
    /** When the check started from here began (ms), until its end is seen. */
    const checkStartedAt = useRef<number | null>(null);
    const wasRunning = useRef(false);
    const planTop = useRef<HTMLDivElement>(null);

    const setView = (v: View) => {
        const nextParams = new URLSearchParams(params);
        if (v === "next") nextParams.delete("view");
        else nextParams.set("view", v);
        nextParams.delete("section");
        setParams(nextParams, { replace: true });
        if (v === "next" || v === "checklist") {
            setListMode(v);
            try {
                window.localStorage.setItem(VIEW_KEY, v);
            } catch {
                /* ignore */
            }
        }
    };

    const openDeliverable = (key: string, group: DeliveryGroup, keepSeq = false) => {
        const nextParams = new URLSearchParams(params);
        nextParams.set("d", key);
        setParams(nextParams, { replace: false });
        setOpenGroup(group);
        if (!keepSeq) setOpenSeq(snapshotFor(group));
    };
    const closeDeliverable = () => {
        const nextParams = new URLSearchParams(params);
        nextParams.delete("d");
        setParams(nextParams, { replace: true });
        setOpenGroup(null);
        setOpenSeq([]);
        setCreating(null);
    };
    const startCreate = (prefill: CreatePrefill) => setCreating((c) => ({ prefill, n: (c?.n ?? 0) + 1 }));
    const endCreate = () => setCreating(null);

    // A docs write (a doc made, shared or approved) can move a deliverable's doc step: read again.
    useEffect(() => (DOCS_WRITE_ON ? subscribeDocs(() => void reload(true)) : undefined), [reload]);

    const openPlan = (section?: PlanSection) => {
        const nextParams = new URLSearchParams(params);
        nextParams.set("view", "plan");
        nextParams.delete("d");
        if (section) nextParams.set("section", section);
        setParams(nextParams, { replace: false });
    };

    // The sections Plan can be opened AT, each by the DOM id of the thing to
    // look at. The outlet names joined them because a refused per-outlet tick
    // has to be able to send staff to the step that clears it (#3985).
    useEffect(() => {
        const section = params.get("section");
        if (view !== "plan" || !section || !isPlanSection(section)) return;
        const t = window.setTimeout(() => {
            const at = document.getElementById(PLAN_SECTION_ID[section]);
            at?.scrollIntoView({ behavior: "smooth", block: "start" });
            // A field is focused as well as scrolled to; a heading is only shown.
            if (section === "outlet-names") document.getElementById(`${PLAN_SECTION_ID[section]}-0`)?.focus();
        }, 250);
        return () => window.clearTimeout(t);
    }, [view, params]);

    const data: DeliveryPayload | null = load.state === "ready" ? load.data : null;
    const groups = useMemo(() => (data ? groupRows(data.deliverables, data.month) : []), [data]);
    const snapshotFor = (group: DeliveryGroup): string[] =>
        view === "month" ? (data?.deliverables.map((d) => d.key) ?? []) : (groups.find((g) => g.key === group)?.rows.map((d) => d.key) ?? []);

    // A contract edit in Plan changes the deliverables: read again when leaving Plan or opening another view.
    const lastView = useRef(view);
    useEffect(() => {
        if (lastView.current !== view) void reload(true);
        lastView.current = view;
    }, [view, reload]);
    const open = data && openKey ? data.deliverables.find((d) => d.key === openKey) ?? null : null;
    const group = open ? openGroup ?? open.group : null;

    // The drawer's previous and next run through the group it opened from, as it stood then
    // (a deep link opens with its own group). A row that has since left the group is skipped.
    const seqIds = open && openSeq.length > 0 ? openSeq : open && group ? snapshotFor(group) : [];
    const titleOf = (key: string) => {
        const d = data?.deliverables.find((x) => x.key === key);
        if (!d) return null;
        if (key !== open?.key && view !== "month" && group && d.group !== group) return null;
        return `${d.code} ${d.label}`;
    };
    const seq = open ? drawerSequence(seqIds, open.key, titleOf) : null;
    const nav = seq && group && !creating ? drawerNav(seq, { group: GROUP_TITLE[group], onMove: (key) => openDeliverable(key, group, true), keys: true }) : undefined;

    const makeDone = (said: string): DrawerDone | null => {
        if (!data || !open || !group) return null;
        const others = seqIds.filter((k) => k !== open.key).map((k) => data.deliverables.find((d) => d.key === k)).filter((d): d is NonNullable<typeof d> => Boolean(d) && d!.group === group);
        const nextRow = others[0] ?? null;
        return {
            said,
            left: others.length,
            group: GROUP_TITLE[group],
            next: nextRow ? { id: nextRow.key, title: `${nextRow.code} ${nextRow.label}` } : null,
            audience: "staff",
            onNext: () => (nextRow ? openDeliverable(nextRow.key, group, true) : closeDeliverable()),
            onDone: closeDeliverable,
        };
    };

    const nextDeliverable = () => {
        if (seq?.next && group) openDeliverable(seq.next.id, group, true);
        else closeDeliverable();
    };

    const openDoc = (docId: string) => onGoTo("reviews", `?doc=${encodeURIComponent(docId)}`);

    // The same rows as the Checking group below it, so "Check now · N waiting" and "Checking N" agree.
    const waitingOnOura = data ? data.deliverables.filter((d) => d.group === "checking").length : 0;
    const runCheck = async () => {
        if (checkBusy || data?.lastCheck.running) return;
        setCheckBusy(true);
        const r = await checkNow(businessId);
        setCheckBusy(false);
        checkStartedAt.current = r.ok ? Date.parse(r.data.startedAt) || Date.now() : null;
        wasRunning.current = false;
        setCheckMsg(r.ok ? "Oura is checking the marked pages now. About a minute on a 600-page site." : r.reason === "rejected" && r.message ? r.message : "The check did not start. Try again.");
    };
    // Say it finished once this check was seen running and then stopped, or once
    // a read shows a check that ended after this one began (a quick check, or a
    // read slow enough to miss the running state).
    const running = Boolean(data?.lastCheck.running);
    const lastCheckAt = data?.lastCheck.at ?? null;
    useEffect(() => {
        const started = checkStartedAt.current;
        const endedSince = started !== null && lastCheckAt !== null && Date.parse(lastCheckAt) >= started;
        if (started !== null && !running && (wasRunning.current || endedSince)) {
            checkStartedAt.current = null;
            setCheckMsg("Checked just now. The list shows what Oura read.");
        }
        wasRunning.current = running;
    }, [running, lastCheckAt]);

    if (!isDeliveryServed) {
        return <p className={cn(TYPE.tb, "text-gray-600")}>Delivery is not available here yet. Fixes and Plan keep working until it is.</p>;
    }

    if (load.state === "loading") {
        return (
            <FoldScope value="staff-delivery">
                <div className="space-y-3" aria-busy>
                    <div className="h-6 w-72 animate-pulse rounded bg-gray-200" />
                    <div className="h-64 animate-pulse rounded-2xl bg-white" />
                </div>
            </FoldScope>
        );
    }
    if (load.state === "failed") {
        return <p className={cn(TYPE.tb, "rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 ring-1 ring-inset ring-amber-200")}>Delivery did not load. Try again in a minute.</p>;
    }
    if (load.state === "none" || !data) {
        return (
            <FoldScope value="staff-delivery">
                <ScreenSentence title="Delivery">No plan yet. Delivery starts once a signed quotation is activated.</ScreenSentence>
                <div className="space-y-5">
                    <NoPlanYet businessName={businessName} onOpenClient={() => onGoTo("reviews")} />
                    <PlanView businessId={businessId} contract={contract} />
                </div>
            </FoldScope>
        );
    }

    const late = data.deliverables.filter((d) => d.actor?.late).length;
    const needsProof = data.deliverables.filter((d) => d.passedWithoutWork).length;
    const sentence = (
        <>
            {data.monthLabel} · {data.doneCount.done} of {data.doneCount.total} done · {data.due ? `${data.phase === "foundation" ? "Handover" : "Report"} ${dayLabel(data.due)}` : "No start date yet"}
            {late > 0 && <span className="text-rose-600"> · {plural(late, "deliverable")} late</span>}
            {needsProof > 0 && <span className="text-amber-700"> · {needsProof} {needsProof === 1 ? "needs" : "need"} proof</span>}
        </>
    );

    return (
        <FoldScope value="staff-delivery">
            <ScreenSentence title="Delivery">{sentence}</ScreenSentence>

            <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-2">
                    {/* On a phone the four views scroll inside their own row, never the page. */}
                    <div className="-mx-1 max-w-full overflow-x-auto px-1 py-0.5">
                    <Seg<View>
                        ariaLabel="How to show Delivery"
                        value={view}
                        onChange={setView}
                        items={[
                            { key: "next", label: "Next steps", icon: ListIcon },
                            ...(LEGACY_VIEWS_ON ? [{ key: "checklist" as const, label: "Checklist", icon: ListChecksIcon }] : []),
                            ...(TASKS_ON ? [{ key: "tasks" as const, label: "Tasks", icon: Table2Icon }] : []),
                            ...(LEGACY_VIEWS_ON ? [{ key: "month" as const, label: "By month", icon: CalendarRangeIcon }] : []),
                            { key: "plan", label: "Plan", icon: NotebookPenIcon },
                        ]}
                    />
                    </div>
                    {data.cameBack > 0 && <Tag tone="rose">Came back {data.cameBack}</Tag>}
                    <span className="ml-auto flex flex-wrap items-center gap-2">
                        {waitingOnOura > 0 && (
                            <PillButton icon={RefreshCwIcon} busy={data.lastCheck.running || checkBusy} writes onClick={() => void runCheck()}>
                                {data.lastCheck.running ? "Checking now" : `Check now · ${waitingOnOura} waiting`}
                            </PillButton>
                        )}
                        {/* With the timeline cards: on Plan (#4727), and on By month while that shows. */}
                        {(view === "month" || view === "plan") && (
                            <PillButton icon={DownloadIcon} onClick={() => setCheckMsg(`Downloaded ${downloadDeliveryTimeline(data)}. Print it to PDF to send.`)}>
                                Download timeline
                            </PillButton>
                        )}
                    </span>
                </div>

                {load.state === "ready" && load.stale && (
                    <p role="status" className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-amber-800")}>
                        The last read did not come through, so this is the list as it was.
                        <button type="button" className="font-medium text-amber-900 underline" onClick={() => void reload(true)}>
                            Try again
                        </button>
                    </p>
                )}

                {checkMsg && (
                    <p role="status" className={cn(TYPE.tc, "m-0 rounded-xl bg-violet-50 px-3 py-2 text-violet-800")}>
                        {checkMsg}
                    </p>
                )}

                {!data.keyPages.confirmed && view !== "plan" && view !== "tasks" && (
                    <div className="flex flex-col items-start gap-3 rounded-2xl bg-amber-50 px-4 py-3 ring-1 ring-inset ring-amber-200 sm:flex-row sm:items-center">
                        <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 text-amber-900")}>
                            Confirm the key pages Oura suggests. S1, S3, S6, S7, A2 and G2 check them.
                        </p>
                        <PillButton variant="primary" onClick={() => openPlan("key-pages")}>
                            Review the key pages
                        </PillButton>
                    </div>
                )}

                {/* Ask Oura (behind VITE_ENABLE_OURA_ASK): agreed answers no draft has started from yet. */}
                {ASK_OURA_ON && role === "oura-staff" && (view === "next" || view === "checklist") && <AskOuraSuggestion businessId={businessId} delivery={data} />}

                {(view === "next" || view === "checklist") && (
                    <FoldCard id="delivery-list" title={view === "checklist" ? "Checklist" : "Next steps"} foldedFact={`${data.doneCount.done} of ${data.doneCount.total} done`}>
                        {view === "checklist" && <SquareKey className="mb-3" tasksDecide={data.deliverables.some((d) => d.noTasks === false)} />}
                        {view === "next" && (
                            <RequestRows businessId={businessId} requests={requests.data} month={data.month} onOpen={(id) => onGoTo("reviews", `?request=${encodeURIComponent(id)}`)} />
                        )}
                        <DeliveryList
                            businessId={businessId}
                            groups={groups}
                            mode={view === "checklist" ? "checklist" : "next"}
                            openKey={openKey}
                            onOpen={openDeliverable}
                            month={data.month}
                            requests={requests.data}
                        />
                    </FoldCard>
                )}

                {view === "tasks" && TASKS_LIVE && <TasksLive key={businessId} businessId={businessId} initialPhase={params.get("phase")} initialOpen={params.get("task")} />}

                {view === "tasks" && !TASKS_LIVE && (
                    <TasksView
                        phases={SAMPLE_PHASES}
                        sheetUrl={SAMPLE_SHEET_URL}
                        syncedAgo="1 min ago"
                        sample
                        initialPhase={params.get("phase") ?? "M1"}
                        initialOpen={params.get("task")}
                    />
                )}

                {view === "month" && (
                    <FoldCard id="delivery-month" title="By month" foldedFact={`${data.termMonths || data.deliverables.reduce((m, d) => Math.max(m, d.month), 0)} months`}>
                        <ByMonth payload={data} openKey={openKey} onOpen={openDeliverable} />
                    </FoldCard>
                )}
                {view === "plan" && (
                    <div ref={planTop}>
                        <PlanView businessId={businessId} contract={contract} />
                    </div>
                )}
                {/* The published timeline: the dates the client is told, and Publish.
                    On Plan since By month was hidden (#4727). */}
                {view === "plan" && timeline}
            </div>

            <Drawer
                open={Boolean(open)}
                onClose={closeDeliverable}
                width={560}
                nav={nav}
                itemKey={creating && open ? `${open.key}:create:${creating.n}` : open?.key}
                back={creating && open ? { label: `${open.code} ${open.label}`, onBack: endCreate } : undefined}
            >
                {open && creating && DOCS_WRITE_ON ? (
                    <CreateFromDelivery
                        key={`${open.key}:create:${creating.n}`}
                        businessId={businessId}
                        businessName={businessName}
                        prefill={creating.prefill}
                        onOpenDoc={openDoc}
                        onNext={startCreate}
                        onClose={endCreate}
                    />
                ) : open && (
                    <DeliverableSheet
                        key={open.key}
                        businessId={businessId}
                        d={open}
                        payload={data}
                        makeDone={makeDone}
                        onApplied={apply}
                        onOpenDoc={openDoc}
                        onGoTo={onGoTo}
                        onOpenPlan={openPlan}
                        onNextDeliverable={nextDeliverable}
                        onCreateDoc={DOCS_WRITE_ON ? startCreate : undefined}
                    />
                )}
            </Drawer>
        </FoldScope>
    );
}
